import { useRef, useState } from 'react';
import type { Subscription } from 'rxjs';
import { useNavigate } from 'react-router-dom';
import { login, register } from '../api/auth.api';
import { HttpError } from '@/shared/api/http';
import { Button } from '@/shared/components/Button';
import { Input } from '@/shared/components/Input';

function LoginForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const subRef = useRef<Subscription | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    subRef.current?.unsubscribe();
    subRef.current = login(email, password).subscribe(
        {
      next: () => {
        setLoading(false);
        navigate('/');
        },
      error: (err) => {
        console.log(err)
        setError('Napačni podatki / Invalid credentials'); setLoading(false);
        },
    }
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">E-pošta / Email</label>
        <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="pilot@example.com" />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Geslo / Password</label>
        <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} />
      </div>
      {error && <p className="text-rose-400 text-sm">{error}</p>}
      <Button type="submit" disabled={loading} className="w-full justify-center">
        {loading ? 'Prijava...' : 'Prijava / Sign in'}
      </Button>
    </form>
  );
}

function RegisterForm({ onSuccess }: { onSuccess: () => void }) {
  const [form, setForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    operator_name: '',
    operator_registration_number: '',
    operator_registration_valid_until: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const subRef = useRef<Subscription | null>(null);

  function set(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    subRef.current?.unsubscribe();
    subRef.current = register(form).subscribe({
      next: () => { setLoading(false); onSuccess(); },
      error: (err) => {
        let msg = 'Registracija ni uspela / Registration failed';
        if (err instanceof HttpError && err.data && typeof err.data === 'object') {
          const parts = Object.values(err.data as Record<string, string[]>).flat();
          if (parts.length > 0) msg = parts.join(' ');
        }
        setError(msg);
        setLoading(false);
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Ime / First name</label>
          <Input required value={form.first_name} onChange={set('first_name')} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Priimek / Last name</label>
          <Input required value={form.last_name} onChange={set('last_name')} />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">E-pošta / Email</label>
        <Input type="email" required value={form.email} onChange={set('email')} placeholder="pilot@example.com" />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Geslo / Password</label>
        <Input type="password" required value={form.password} onChange={set('password')} />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Telefon / Phone</label>
        <Input type="tel" value={form.phone} onChange={set('phone')} placeholder="+386 31 123 456" />
      </div>
      <div className="border-t border-slate-700 pt-3">
        <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Operator</div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Naziv operatorja / Operator name</label>
            <Input required value={form.operator_name} onChange={set('operator_name')} placeholder="SLO-OP-XXXXX" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Reg. številka / Reg. number</label>
            <Input required value={form.operator_registration_number} onChange={set('operator_registration_number')} placeholder="SLO-OP-12345" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Veljavnost reg. / Reg. valid until</label>
            <Input type="date" required value={form.operator_registration_valid_until} onChange={set('operator_registration_valid_until')} />
          </div>
        </div>
      </div>
      {error && <p className="text-rose-400 text-sm">{error}</p>}
      <Button type="submit" disabled={loading} className="w-full justify-center">
        {loading ? 'Registracija...' : 'Registracija / Register'}
      </Button>
    </form>
  );
}

export function LoginPage() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [registered, setRegistered] = useState(false);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-800 border border-slate-700 rounded-xl p-8 flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-xl">🛸</div>
          <div>
            <div className="text-lg font-bold text-slate-100">Najava Leta</div>
            <div className="text-xs text-slate-500">UAS Flight Notification System</div>
          </div>
        </div>

        <div className="flex border border-slate-700 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => { setMode('login'); setRegistered(false); }}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${mode === 'login' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
          >
            Prijava / Sign in
          </button>
          <button
            type="button"
            onClick={() => { setMode('register'); setRegistered(false); }}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${mode === 'register' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
          >
            Registracija / Register
          </button>
        </div>

        {registered && (
          <div className="bg-green-900/40 border border-green-700 rounded-lg p-3 text-green-300 text-sm">
            Registracija uspešna! Prijavite se. / Registration successful! Please sign in.
          </div>
        )}

        {mode === 'login' ? (
          <LoginForm />
        ) : (
          <RegisterForm onSuccess={() => { setRegistered(true); setMode('login'); }} />
        )}
      </div>
    </div>
  );
}
